import { Box, Stack, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { ReactNode } from "react";
import { useSearchParams } from "react-router";
import { PlanId, PlanIdProvider } from "components/common/boundary/PlanId";
import { ProductionTable } from "components/plan/table/production/ProductionTable";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  useGetPlanQuery,
  useGetPlanResultsQuery,
  useGetSearchContextQuery,
  useGetSearchQuery,
} from "src/store/api/generatedApi";
import { ArtefactShell, ArtefactState } from "./ArtefactShell";
import {
  ArtefactPeriodInput,
  buildRawData,
  buildTitle,
  hasInventory,
  mixColumnWidthPx,
  parsePeriods,
  parsePlanId,
  periodLabel,
  RecipeCompositionArtefactData,
  tableWidthPx,
} from "./recipeComposition";

export const RecipeCompositionArtefactPage = () => {
  const [searchParams] = useSearchParams();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();

  const planId = parsePlanId(searchParams.get("planId"));
  const requestedPeriods = parsePeriods(searchParams.get("periods"));
  const periodList = requestedPeriods ?? [];

  const periodArg = (slot: number) => {
    const period = periodList[slot];
    return planId !== null && period !== undefined
      ? { tenantName, planId, period }
      : skipToken;
  };

  const period0 = useGetPlanResultsQuery(periodArg(0));
  const period1 = useGetPlanResultsQuery(periodArg(1));
  const period2 = useGetPlanResultsQuery(periodArg(2));
  const period3 = useGetPlanResultsQuery(periodArg(3));
  const period4 = useGetPlanResultsQuery(periodArg(4));
  const period5 = useGetPlanResultsQuery(periodArg(5));
  const periodQueries = [period0, period1, period2, period3, period4, period5];
  const activeQueries = periodQueries.slice(0, periodList.length);

  const planQuery = useGetPlanQuery(
    planId !== null ? { tenantName, planId } : skipToken
  );
  const plan = planQuery.data;

  const searchId = plan?.search_id;
  const searchQuery = useGetSearchQuery(
    searchId !== undefined ? { tenantName, searchId } : skipToken
  );
  const search = searchQuery.data;

  const contextId = search?.context_id;
  const contextQuery = useGetSearchContextQuery(
    contextId !== undefined
      ? { tenantName, searchContextId: contextId }
      : skipToken
  );
  const context = contextQuery.data;

  const state: ArtefactState = (() => {
    if (planId === null || requestedPeriods === null) {
      return "error";
    }
    if (planQuery.isError || searchQuery.isError || contextQuery.isError) {
      return "error";
    }
    if (activeQueries.some((query) => query.isError)) {
      return "error";
    }
    if (!plan) {
      return "loading";
    }
    const periodNumbers = new Set(
      plan.summary.period_summary.map((summary) => summary.period)
    );
    if (requestedPeriods.some((period) => !periodNumbers.has(period))) {
      return "error";
    }
    if (!search || !context) {
      return "loading";
    }
    if (activeQueries.some((query) => !query.data)) {
      return "loading";
    }
    return "ready";
  })();

  const { rawData, content } = ((): {
    rawData: RecipeCompositionArtefactData | null;
    content: ReactNode;
  } => {
    if (
      state !== "ready" ||
      !plan ||
      !context ||
      requestedPeriods === null ||
      planId === null
    ) {
      return { rawData: null, content: null };
    }

    const materials = sorted(
      context.materials,
      (material) => material.ordering
    );
    const chemistryGroups = sorted(context.chef_groups, (group) => group.name);
    const productGroups = sorted(context.product_groups, (group) => group.name);
    const summaryByPeriod = new Map(
      plan.summary.period_summary.map((summary) => [summary.period, summary])
    );

    const orderedPeriods: ArtefactPeriodInput[] = requestedPeriods.flatMap(
      (period, index) => {
        const summary = summaryByPeriod.get(period);
        if (summary === undefined) {
          return [];
        }
        const header = `${periodLabel(summary, "ddd MMM D")}: ${summary.num_heats} ${t("heats")}`;
        return [
          {
            summary,
            date: periodLabel(summary, "MMM D"),
            header,
            results: activeQueries[index]?.data ?? null,
          },
        ];
      }
    );

    const rawData = buildRawData({
      planId,
      title: buildTitle(orderedPeriods.map((input) => input.date)),
      periods: orderedPeriods,
      mixSummaries: plan.summary.mix_summary,
      mixMetadata: plan.mix_metadata,
    });

    const content = (
      <PlanIdProvider planId={planId as PlanId}>
        <Stack
          sx={{
            p: 1,
            gap: 3,
            width: rawData.content_width_px,
            alignItems: "center",
          }}
        >
          {orderedPeriods.map((input, index) => {
            const periodResults =
              input.results?.periods[input.summary.period.toString()] ?? null;
            const hasMixes = rawData.periods[index]?.has_mixes ?? false;
            return (
              <Stack
                key={input.summary.period}
                gap={1}
                sx={{ alignItems: "center" }}
              >
                <Typography variant="h5">{input.header}</Typography>
                {periodResults && hasMixes ? (
                  <Box
                    sx={{
                      width: tableWidthPx(
                        rawData.max_mix_columns,
                        hasInventory(rawData.periods)
                      ),
                    }}
                  >
                    <ProductionTable
                      variant="static"
                      hideEmptyMaterialRows
                      mixColumnWidthPx={mixColumnWidthPx(
                        rawData.periods[index]?.mixes.length ?? 0,
                        rawData.max_mix_columns
                      )}
                      productGroups={productGroups}
                      chemistryGroups={chemistryGroups}
                      materials={materials}
                      mixResults={periodResults.mixes}
                      productGroupResults={periodResults.product_group_results}
                      mixSummaries={plan.summary.mix_summary}
                      periodMaterialSummaries={
                        input.results?.summary.period_material_summary ?? []
                      }
                      period={input.summary.period}
                      mixMetadata={plan.mix_metadata}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2">{t("noMixesFound")}</Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </PlanIdProvider>
    );

    return { rawData, content };
  })();

  return (
    <ArtefactShell state={state} rawData={rawData}>
      {content}
    </ArtefactShell>
  );
};
